'use client'

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

const CONSENT_KEY = 'soleno_cookie_consent'

type Consent = 'accepted' | 'declined'

export function getCookieConsent(): Consent | null {
  if (typeof window === 'undefined') return null
  return localStorage.getItem(CONSENT_KEY) as Consent | null
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const consent = getCookieConsent()
    if (!consent) setVisible(true)
  }, [])

  function handleAccept() {
    localStorage.setItem(CONSENT_KEY, 'accepted')
    setVisible(false)
    enableAnalytics()
  }

  function handleDecline() {
    localStorage.setItem(CONSENT_KEY, 'declined')
    setVisible(false)
    disableAnalytics()
  }

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-4 left-4 right-4 md:bottom-6 md:left-auto md:right-6 md:max-w-md z-[60] rounded-2xl border border-white/[0.12] bg-charcoal-900/70 backdrop-blur-xl shadow-2xl shadow-black/30 p-6"
        >
          <p className="text-sm text-white/80 leading-relaxed mb-5">
            We use cookies to analyze site traffic and improve your experience. By accepting, you consent to our use of analytics cookies.{' '}
            <a href="/privacy" className="text-white/50 underline underline-offset-2 hover:text-white/80 transition-colors after:hidden">
              Privacy Policy
            </a>
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={handleAccept}
              className="flex-1 px-5 py-2.5 rounded-xl bg-white text-charcoal-900 text-sm font-medium hover:bg-white/90 transition-colors"
            >
              Accept
            </button>
            <button
              onClick={handleDecline}
              className="flex-1 px-5 py-2.5 rounded-xl border border-white/[0.12] text-white/60 text-sm font-medium hover:text-white/90 hover:border-white/20 transition-colors"
            >
              Decline
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function enableAnalytics() {
  if (typeof window === 'undefined') return
  window.gtag?.('consent', 'update', {
    analytics_storage: 'granted',
  })
}

function disableAnalytics() {
  if (typeof window === 'undefined') return
  window.gtag?.('consent', 'update', {
    analytics_storage: 'denied',
  })

  document.cookie.split(';').forEach((c) => {
    const name = c.split('=')[0].trim()
    if (name.startsWith('_ga') || name.startsWith('_gid')) {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${window.location.hostname}`
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
    }
  })
}
